import React, { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SortingRule } from "react-table";
import { Col, Row } from "reactstrap";
import ButtonComponent from "../../components/ButtonComponent";
import InputComponent from "../../components/InputComponent";
import Layout from "../../components/Layout/Layout";
import TableComponent, { OperationsCell } from "../../components/TableComponent";
import Routes from "../../helpers/Routes";
import Toast from "../../helpers/Toast";
import { Utilities } from "../../helpers/Utilities";
import { Parameter } from "../../models/Parameter";
import { SubcontractorSearchResultDTO } from "../../models/Subcontractor/SubcontractorSearchResultDTO";
import { SubcontractorService } from "../../services/SubcontractorService";

export default function SubcontractorIndex() {
    const intl = useIntl();
    const navigate = useNavigate();
    const subcontractorService = new SubcontractorService();
    const pageSizes: number[] = [5, 15, 30];
    const [data, setData] = useState<SubcontractorSearchResultDTO[]>([]);
    const [loading, setLoading] = useState(false);
    const [pageCount, setPageCount] = useState(1);
    const [searchParams] = useSearchParams();

    const [page] = useState<number>(Utilities.LoadPageFromURLQuery("page"));
    const [pageSize] = useState<number>(
        Utilities.LoadPageSizeFromURLQuery(pageSizes)
    );

    //Search Parameters
    const [companyName, setCompanyName] = useState<string>(
        Utilities.LoadParameterFromURLQuery("name", "string", "")
    );
    const [vatNumber, setVatNumber] = useState<string>(
        Utilities.LoadParameterFromURLQuery("vatNumber", "string", "")
    );

    const [currentPage, setCurrentPage] = useState<number>();
    const [currentPageSize, setCurrentPageSize] = useState<number>();
    const [currentSorting, setCurrentSorting] = useState<SortingRule<object>[] | null>(null);
    const [goToPage, setGoToPage] = useState<number>();
    const [initialSortBy] = useState<SortingRule<object>[]>(
        Utilities.LoadSortParametersFromURLQuery("sortField", "sortOrder")
    );
    const [reloadSearch, setReloadSearch] = useState<boolean>(false);

    const columns = React.useMemo(
        () => [
            {
                Header: "",
                accessor: " ",
                Cell: (props: any) =>
                    OperationsCell(props.cell.row.original.id, "subcontractor"),
                width: 5,
            },
            {
                Header: <FormattedMessage id="Screens.Subcontractors.Table.CompanyName" />,
                accessor: "companyName",
                width: 20,
            },
            {
                Header: <FormattedMessage id="Screens.Subcontractors.Table.Address" />,
                accessor: "address",
                width: 20,
            },
            {
                Header: <FormattedMessage id="Screens.Subcontractors.Table.VatNumber" />,
                accessor: "vatNumber",
                width: 17,
            },
            {
                Header: <FormattedMessage id="Screens.Subcontractors.Table.PhoneNumber" />,
                accessor: "phoneNumber",
                width: 18,
            },
            {
                Header: <FormattedMessage id="Screens.Subcontractors.Table.Email" />,
                accessor: "email",
                width: 20,
            }
        ],
        []
    );

    const fetchData = async (
        page: number,
        pageSize: number,
        sortBy: SortingRule<object>[] | null
    ) => {
        //Atualizamos na history do browser a current page e pageSize

        var searchUrl: string = `?page=${page}&pageSize=${pageSize}` +
            `${Utilities.AddParamToQueryIfValue("companyName", "string", companyName)}` +
            `${Utilities.AddParamToQueryIfValue("vatNumber", "string", vatNumber)}`;

        if (sortBy != null && sortBy.length > 0) {
            searchUrl += `${Utilities.AddParamToQueryIfValue(
                "sortField",
                "string",
                sortBy[0].id
            )}${Utilities.AddParamToQueryIfValue(
                "sortOrder",
                "string",
                sortBy[0].desc === true ? "DESC" : "ASC"
            )}`;
        }

        //Se antes não existirem parâmetros de pesquisa, significa que o user veio para aqui de uma rota, então para não ficar com duas rotas duplicadas, fazemos replace à rota

        if (searchParams.toString() === "") {
            navigate(`/subcontractors${searchUrl}`, { replace: true });
        } else {
            //Se já existirem parâmetros de pesquisa, não fazemos replace mas push
            navigate(`/subcontractors${searchUrl}`);
        }

        setLoading(true);

        var response = await subcontractorService.GetAll(
            mountSearchParameters(),
            Utilities.MountSortParameters(sortBy),
            page,
            pageSize
        );

        if (response.success !== true) {
            setData([]);
            setPageCount(0);
            setLoading(false);
            setCurrentPageSize(5);
            setCurrentPage(1);
            setCurrentSorting(null);
            Toast.Show("error", response.message);
            return;
        }

        setData(response.items);
        setPageCount(response.totalPages);
        setLoading(false);
        setCurrentPageSize(pageSize);
        setCurrentPage(page);
        setCurrentSorting(sortBy);
        setGoToPage(0);

        //Se a pesquisa tiver retornado múltiplas páginas e a página atual for superior ao nº de páginas que chegaram mandamos o user para a página 1
        if (response.totalPages > 0 && page > response.totalPages) {
            setGoToPage(1);
        }
    };

    //Método para montar os parâmetros de pesquisa
    const mountSearchParameters = (): Parameter[] => {
        var parameters: Parameter[] = [];
        parameters.push(new Parameter("companyName", companyName));
        parameters.push(new Parameter("vatNumber", vatNumber));

        return parameters;
    };

    const onEnter = () => {
        fetchData(currentPage ?? 1, currentPageSize ?? 5, currentSorting);
    };
    const onButtonClick = async () => {
        setCurrentPage(1);
        setGoToPage(1);
        fetchData(currentPage ?? 1, currentPageSize ?? 5, currentSorting);
    };
    const onRemoveFiltersClick = async () => {
        setCurrentPage(1);
        setGoToPage(1);
        setCompanyName("");
        setVatNumber("");
        setReloadSearch(true);
    };

    useEffect(() => {
        if (reloadSearch === true) {
            fetchData(currentPage ?? 1, currentPageSize ?? 5, currentSorting);
            setReloadSearch(false);
        }
        return () => { };
    }, [reloadSearch]);

    const onNewTaskClick = () => {
        navigate(Routes.subcontractors.create);
    };

    return (
        <>
            <Layout>
                <div className="pageContainer">
                    <div className="pageHeader">
                        <span className="pageTitle">
                            <FormattedMessage id="Screens.Subcontractors.Title" />
                        </span>
                        <Row>
                            <Col
                                xl={2}
                                lg={3}
                                md={3}
                                sm={12}
                                xs={12}
                                style={{ marginTop: "1em" }}
                            >
                                <ButtonComponent
                                    text={intl.formatMessage({ id: "Screens.Subcontractors.Button.CreateSubcontractor" })}
                                    onClick={onNewTaskClick}
                                    disabled={false} />
                            </Col>
                        </Row>
                    </div>

                    <div className="pageBody">
                        <Row>
                            <InputComponent
                                xl={2}
                                lg={3}
                                md={5}
                                sm={12}
                                xs={12}
                                style={{ width: "100%" }}
                                type={"text"}
                                onEnter={onEnter}
                                value={companyName}
                                label={intl.formatMessage({ id: "Screens.Subcontractors.SearchField.CompanyName" })}
                                readOnly={false}
                                onChange={(t: string) => setCompanyName(t)}
                            />
                            <InputComponent
                                xl={2}
                                lg={3}
                                md={5}
                                sm={12}
                                xs={12}
                                style={{ width: "100%" }}
                                type={"text"}
                                onEnter={onEnter}
                                value={vatNumber}
                                label={intl.formatMessage({ id: "Screens.Subcontractors.SearchField.VatNumber" })}
                                readOnly={false}
                                onChange={(t: string) => setVatNumber(t)}
                            />
                        </Row>

                        <Row>
                            <Col
                                xl={6}
                                lg={6}
                                md={6}
                                sm={12}
                                xs={12}
                                style={{ marginTop: "1em", display: "inline-flex", gap: "1rem" }}
                            >
                                <ButtonComponent
                                    text={intl.formatMessage({ id: "Common.Buttons.Search" })}
                                    onClick={onButtonClick}
                                    style={{ minWidth: "100px" }}
                                />
                                <ButtonComponent
                                    text={intl.formatMessage({ id: "Common.Buttons.RemoveFilters" })}
                                    onClick={onRemoveFiltersClick}
                                    style={{ minWidth: "113px" }}
                                />
                            </Col>
                        </Row>

                        <div className="pageComponent" style={{ width: "100%" }}>
                            <TableComponent
                                goToPage={goToPage}
                                loading={loading}
                                pageCount={pageCount}
                                columns={columns}
                                manualPagination={true}
                                fetchData={fetchData}
                                pageSizes={pageSizes}
                                initialPage={page}
                                initialPageSize={pageSize}
                                initialSortBy={initialSortBy}
                                data={data}
                                dontOrderFirstCollumn={true}
                            />
                        </div>
                    </div>
                </div>
            </Layout>
        </>
    );
}
